import { useCallback, useEffect, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { theme, type GlobalToken } from 'antd';
import { ScrollArrow, type ScrollSide } from './ScrollArrow';

/** Share of the visible row one arrow click reveals. */
const SCROLL_PAGE_RATIO = 0.7;

/** Width of the gradient that hints at items hidden past each edge. */
const FADE_WIDTH = 72;

/**
 * Tracks which edges of `scrollEl` still have items to reveal. Watches the
 * scroller's own box and its content's (`contentSelector`, else the first
 * child), since both widths change independently.
 */
const useScrollEdges = (
  scrollEl: HTMLElement | null,
  contentSelector: string | undefined,
) => {
  const [edges, setEdges] = useState({ canLeft: false, canRight: false });

  useEffect(() => {
    if (!scrollEl) return;

    const update = () => {
      const canLeft = scrollEl.scrollLeft > 1;
      const canRight =
        scrollEl.scrollLeft + scrollEl.clientWidth < scrollEl.scrollWidth - 1;
      setEdges((curr) =>
        curr.canLeft === canLeft && curr.canRight === canRight
          ? curr
          : { canLeft, canRight },
      );
    };

    update();
    scrollEl.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(scrollEl);
    const content = contentSelector
      ? scrollEl.querySelector(contentSelector)
      : scrollEl.firstElementChild;
    if (content) observer.observe(content);

    return () => {
      scrollEl.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [scrollEl, contentSelector]);

  return edges;
};

/**
 * Scrolls `container` by the smallest amount that brings `el` fully into view,
 * horizontally. Compares rendered boxes rather than `offsetLeft`, which would
 * assume `el`'s offsetParent is the container.
 */
const scrollIntoViewX = (container: HTMLElement, el: HTMLElement) => {
  const elBox = el.getBoundingClientRect();
  const viewBox = container.getBoundingClientRect();
  const overflowLeft = elBox.left - viewBox.left;
  const overflowRight = elBox.right - viewBox.right;

  if (overflowLeft < 0) {
    container.scrollBy({ left: overflowLeft, behavior: 'smooth' });
  } else if (overflowRight > 0) {
    container.scrollBy({ left: overflowRight, behavior: 'smooth' });
  }
};

/** Brings a partially hidden active item fully into view. */
const useScrollActiveIntoView = (
  scrollEl: HTMLElement | null,
  activeKey: string | undefined,
  activeSelector: string | undefined,
) => {
  useEffect(() => {
    if (!scrollEl || !activeSelector) return;
    const item = scrollEl.querySelector<HTMLElement>(activeSelector);
    if (!item) return;
    scrollIntoViewX(scrollEl, item);
  }, [scrollEl, activeKey, activeSelector]);
};

export interface ScrollableRowProps {
  children: ReactNode;
  /** Changes when the active item changes, so it's scrolled into view. */
  activeKey?: string;
  /** Matches the active item inside the scroller. */
  activeSelector?: string;
  /**
   * Element inside `children` that does the scrolling (e.g. `.ant-tabs-nav`).
   * Omit it and the row wraps `children` in its own scroller.
   */
  scrollSelector?: string;
  /** Content whose width changes are watched. Defaults to the scroller's first child. */
  contentSelector?: string;
  /** Color the edge fades blend from. Defaults to `colorBgContainer`. */
  fadeColor?: string;
  /** Space kept free under the fades and arrows, e.g. a row's bottom border. */
  bottomInset?: number;
  className?: string;
}

/**
 * Horizontal row with edge fades and scroll arrows on whichever side still
 * has hidden items. Used for the Knowledge Hub tabs and the document switcher.
 */
export const ScrollableRow = ({
  children,
  activeKey,
  activeSelector,
  scrollSelector,
  contentSelector,
  fadeColor,
  bottomInset = 0,
  className,
}: ScrollableRowProps) => {
  const { token } = theme.useToken();
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);

  const attachWrap = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return setScrollEl(null);
      setScrollEl(
        scrollSelector
          ? node.querySelector<HTMLElement>(scrollSelector)
          : (node.firstElementChild as HTMLElement | null),
      );
    },
    [scrollSelector],
  );

  const { canLeft, canRight } = useScrollEdges(scrollEl, contentSelector);
  useScrollActiveIntoView(scrollEl, activeKey, activeSelector);

  const scrollByPage = (direction: 1 | -1) => {
    if (!scrollEl) return;
    scrollEl.scrollBy({
      left: direction * Math.round(scrollEl.clientWidth * SCROLL_PAGE_RATIO),
      behavior: 'smooth',
    });
  };

  const fade = fadeColor ?? token.colorBgContainer;

  return (
    <StyledRowWrap ref={attachWrap} className={className}>
      {scrollSelector ? children : <StyledScroller>{children}</StyledScroller>}
      <StyledFade
        $side="left"
        $visible={canLeft}
        $token={token}
        $color={fade}
        $bottomInset={bottomInset}
      />
      <StyledFade
        $side="right"
        $visible={canRight}
        $token={token}
        $color={fade}
        $bottomInset={bottomInset}
      />
      <ScrollArrow
        side="left"
        visible={canLeft}
        token={token}
        bottomInset={bottomInset}
        onClick={() => scrollByPage(-1)}
      />
      <ScrollArrow
        side="right"
        visible={canRight}
        token={token}
        bottomInset={bottomInset}
        onClick={() => scrollByPage(1)}
      />
    </StyledRowWrap>
  );
};

// ─── Styled components ───────────────────────────────────────────────────────

/** Positioning context for the fades and arrows. */
const StyledRowWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

/** Own scroller when no `scrollSelector` is given; items keep their full width. */
const StyledScroller = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }
`;

interface StyledFadeProps {
  $token: GlobalToken;
  $side: ScrollSide;
  $visible: boolean;
  $color: string;
  $bottomInset: number;
}

/** Fades the row out on whichever side still has items to scroll to. */
const StyledFade = styled.div<StyledFadeProps>`
  position: absolute;
  top: 0;
  bottom: ${({ $bottomInset }) => $bottomInset}px;
  ${({ $side }) => $side}: 0;
  width: ${FADE_WIDTH}px;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${({ $token }) => $token.motionDurationMid} ease;
  background: ${({ $color, $side }) =>
    `linear-gradient(to ${$side === 'left' ? 'right' : 'left'}, ${$color} 45%, transparent)`};
`;
